// ─── « Fournisseurs IA » + « Comparer les modèles » (owner, 2026-10-23) ─────
// Worker routes/aiProviders.ts (settings) and routes/aiCompare.ts (comparisons),
// migration 264. Admin only on the server. A file of its own, like
// lib/loginSecurityApi.ts, so parallel work on lib/api.ts never collides.
//
// ⚠️ NO KEY EVER COMES BACK. The browser sends a key once; afterwards it only
// ever sees `last4` and where the key comes from.
import { apiFetch } from './api';

export type ProviderId = 'openrouter' | 'anthropic';
export type KeySlot = ProviderId | 'anthropic_admin';

export interface PublicKeyInfo {
  provider: KeySlot;
  /** 'stored' = saved here; 'env' = a worker secret; 'none'. */
  source: 'stored' | 'env' | 'none';
  last4: string | null;
  updatedAt: string | null;
}

export interface ProviderCard {
  provider: ProviderId;
  enabled: boolean;
  monthlyBudgetUsd: number | null;
  monthSpentUsd: number;
  key: PublicKeyInfo;
  /** Claude only: the workspace an organisation-wide key bills (wrkspc_…). */
  workspaceId?: string | null;
}

export interface PriceRow { input: number; write5m: number; write1h: number; cacheRead: number; output: number }
export interface ModelPriceRow extends PriceRow {
  model: string;
  longAbove: number | null;
  long: PriceRow | null;
}

export interface CreditState {
  amountUsd: number | null;
  start: string | null;   // YYYY-MM-DD
  end: string | null;     // YYYY-MM-DD
  usedUsd: number;
  leftUsd: number | null;
  pctUsed: number | null; // 0..100
  daysLeft: number | null;
  /** USD/day over the last 7 days. */
  rate7dUsd: number;
  /** « le crédit sera épuisé le … » at the 7-day rate; null when it will not run out in the period. */
  runOutDate: string | null;
  runsOutBeforeEnd: boolean;
}

export interface ClaudeCheck {
  day: string;
  source: 'admin_api' | 'console';
  periodFrom: string;
  anthropicUsd: number;
  oursUsd: number;
  gapPct: number | null;
  flagged: boolean;
}

export interface Hop { provider: ProviderId; model: string | null }

export interface TaskRoute {
  primaryProvider: ProviderId;
  primaryModel: string | null;
  fallbackProvider: ProviderId | null;
  fallbackModel: string | null;
  imageLongSide: number | null;
}

export interface TaskRow {
  feature: string;
  /** What runs today (OpenRouter model the code / settings ask for). */
  currentModel: string;
  vision: boolean;
  audio: boolean;
  comparable: boolean;
  /** null = default (OpenRouter, current model, no fallback). */
  route: TaskRoute | null;
  calls30d: number;
  cost30dUsd: number;
  avgInputTokens: number | null;
  avgOutputTokens: number | null;
  /** USD per month on each Claude model at the same traffic (null = no data). */
  projectedUsd: Record<string, number | null>;
  /** Share of the input assumed read from cache in `projectedUsd` (agent_reply only, else 0). */
  cacheShare: number;
}

export interface ReplyRules {
  money_words: string[];
  complaint_words: string[];
  edit_words: string[];
  easy_words: string[];
  darija_markers: string[];
  max_easy_chars: number;
  max_easy_questions: number;
  sticky_minutes: number;
}

export interface ReplyRouterCfg {
  provider: ProviderId;
  enabled: boolean;
  easyModel: string | null;
  complexModel: string | null;
  /** Effective rules (defaults filled in). */
  rules: ReplyRules;
}

export interface AgentEstimate {
  calls30d: number;
  avgInputTokens: number | null;
  avgOutputTokens: number | null;
  /** Share of inbound messages the rules call « easy » over the last 30 days (0..1). */
  easyShare: number | null;
  /** Share of the input read from cache (system prompt + Brain), an assumption shown as such. */
  cacheShare: number;
  currentUsd: number;
  allSonnetUsd: number | null;
  mixedUsd: number | null;
  /** Total of every task's projection vs the Claude budget, with the 30 % buffer. */
  totalProjectedUsd: number | null;
  totalWithBufferUsd: number | null;
}

export interface AiProvidersState {
  /** The encryption secret exists (AI_KEYS_ENC_KEY): keys can be saved. */
  encReady: boolean;
  fxUsd: number;
  providers: Record<ProviderId, ProviderCard>;
  anthropicAdminKey: PublicKeyInfo;
  prices: ModelPriceRow[];
  credit: CreditState;
  consoleAmountUsd: number | null;
  consoleAmountDate: string | null;
  lastCheck: ClaudeCheck | null;
  tasks: TaskRow[];
  replyRouter: Record<ProviderId, ReplyRouterCfg>;
  agentEstimate: AgentEstimate;
  models: { anthropic: Array<{ id: string; label: string }>; openrouter: string[] };
}

export interface ProviderPatch {
  enabled?: boolean;
  monthlyBudgetUsd?: number | null;
  creditUsd?: number | null;
  creditStart?: string | null;
  creditEnd?: string | null;
  consoleAmountUsd?: number | null;
  consoleAmountDate?: string | null;
  workspaceId?: string | null;
}

export interface TestResult { ok: boolean; ms: number; error?: string; model?: string }

// ─── comparisons ─────────────────────────────────────────────────────────────

/** `model: 'mixed'` on anthropic = the WhatsApp easy/complex router (Haiku/Sonnet). */
export interface CompareHop { provider: ProviderId; model: string | null }

export interface CompareTask {
  task: string;
  /** What runs today — the « ancien » side, pre-selected. */
  current: CompareHop;
  currentLabel: string;
  /** Picks per case kind for this task, e.g. ['arabic_script','money','angry','low_res']. */
  caseKinds: string[];
}

export interface CompareRun {
  id: string;
  created_at: string;
  task: string;
  old_provider: ProviderId; old_model: string | null;
  new_provider: ProviderId; new_model: string | null;
  case_count: number;
  cap_usd: number;
  estimate_usd: number | null;
  spent_usd: number;
  done_count: number;
  status: 'queued' | 'running' | 'done' | 'stopped' | 'failed';
  error: string | null;
  finished_at: string | null;
}

export interface FaceBox { x: number; y: number; w: number; h: number; label?: string }

export interface CompareCase {
  id: string;
  run_id: string;
  task: string;
  case_ref: string;
  tags: string[];
  input_summary: string | null;
  /** face_finder: { imageUrl, width, height, oldBoxes: FaceBox[], newBoxes: FaceBox[] } (boxes as 0..1 fractions). photo_vision: { imageUrl }. */
  input_meta: Record<string, unknown> | null;
  status: 'pending' | 'running' | 'done' | 'failed';
  old_output: string | null; new_output: string | null;
  old_model: string | null; new_model: string | null;
  old_cost_usd: number | null; new_cost_usd: number | null;
  old_ms: number | null; new_ms: number | null;
  old_error: string | null; new_error: string | null;
  old_route: 'easy' | 'complex' | 'escalated' | null;
  new_route: 'easy' | 'complex' | 'escalated' | null;
  flagged: boolean;
  flag_reasons: string[];
  verdict: 'old' | 'new' | 'same' | null;
}

export interface CompareEstimate { cases: number; estimateUsd: number; capUsd: number; perCaseUsd: { old: number | null; new: number | null } }

const json = (body: unknown) => ({ method: 'PUT', body: JSON.stringify(body) });

export const aiProvidersApi = {
  state: () => apiFetch<AiProvidersState>('/ai-providers/state'),
  saveKey: (slot: KeySlot, key: string) => apiFetch<{ key: PublicKeyInfo }>(`/ai-providers/keys/${slot}`, json({ key })),
  deleteKey: (slot: KeySlot) => apiFetch<{ key: PublicKeyInfo }>(`/ai-providers/keys/${slot}`, { method: 'DELETE' }),
  test: (slot: KeySlot) => apiFetch<TestResult>(`/ai-providers/test/${slot}`, { method: 'POST' }),
  saveProvider: (p: ProviderId, patch: ProviderPatch) => apiFetch<{ ok: true }>(`/ai-providers/providers/${p}`, json(patch)),
  savePrice: (row: ModelPriceRow) => apiFetch<{ ok: true }>(`/ai-providers/prices/${encodeURIComponent(row.model)}`, json(row)),
  saveRoute: (feature: string, route: TaskRoute) => apiFetch<{ ok: true }>(`/ai-providers/routes/${encodeURIComponent(feature)}`, json(route)),
  resetRoute: (feature: string) => apiFetch<{ ok: true }>(`/ai-providers/routes/${encodeURIComponent(feature)}`, { method: 'DELETE' }),
  saveReplyRouter: (p: ProviderId, cfg: Omit<ReplyRouterCfg, 'provider'>) => apiFetch<{ ok: true }>(`/ai-providers/reply-router/${p}`, json(cfg)),

  compareTasks: () => apiFetch<{ tasks: CompareTask[] }>('/ai-compare/tasks'),
  compareEstimate: (task: string, oldHop: CompareHop, newHop: CompareHop, count: number) =>
    apiFetch<CompareEstimate>('/ai-compare/estimate', { method: 'POST', body: JSON.stringify({ task, old: oldHop, new: newHop, count }) }),
  startRun: (task: string, oldHop: CompareHop, newHop: CompareHop, count: number, capUsd: number) =>
    apiFetch<{ run: CompareRun }>('/ai-compare/runs', { method: 'POST', body: JSON.stringify({ task, old: oldHop, new: newHop, count, capUsd }) }),
  runs: () => apiFetch<{ runs: CompareRun[] }>('/ai-compare/runs'),
  run: (id: string) => apiFetch<{ run: CompareRun; cases: CompareCase[] }>(`/ai-compare/runs/${id}`),
  stopRun: (id: string) => apiFetch<{ ok: true }>(`/ai-compare/runs/${id}/stop`, { method: 'POST' }),
  verdict: (caseId: string, verdict: CompareCase['verdict']) => apiFetch<{ ok: true }>(`/ai-compare/cases/${caseId}/verdict`, json({ verdict })),
};
